import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { IconBadge, panelTopHairline } from '../../ui/common';

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 24px);
  background: var(--panel-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-color);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
  position: relative;
  overflow: visible;
  min-height: calc(100vh - 48px);
  /* Reszta panelu (PanelContainer, ui/common.js) dostaje przy wejsciu na strone
     delikatny fade+wjazd z dolu. slideIn byl tu zdefiniowany od dawna (uzywany przez
     Panel nizej), ale ROOT Container nigdy go nie dostal. */
  animation: ${slideIn} 0.6s ease-out;

  @media (min-width: 1400px) {
    max-width: 1600px;
    padding: 24px 32px;
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
  }

  ${panelTopHairline}
`;

export const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

export const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 2.25rem;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.025em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

`;

export const TitleIcon = styled.div`
  color: var(--info);
`;

// Plakietka ikony w nagłówku panelu — wspólny wzorzec z ui/common.js (IconBadge), zamiast
// lokalnej kopii pełnej niebieskiej wypełnionej ikony. `PanelIcon` zostaje pod tą samą nazwą
// (index.js jej nie zmienia), ale wołający przekazuje teraz `$tone="steel"` itp. zamiast
// `bg="var(--primary)"` — patrz komentarz przy ICON_TONES w ui/common.js. (`FormPanelIcon`
// ma analogiczny alias niżej, przy swojej pierwotnej definicji.)
export const PanelIcon = IconBadge;

export const Subtitle = styled.p`
  margin: 0;
  font-size: 1.125rem;
  color: var(--text-muted);
  font-weight: 400;
`;

export const Panel = styled.section`
  /* Komorka siatki ma domyslnie min-width: auto, wiec nie zweza sie ponizej zawartosci,
     tylko rozpycha strone. Ten sam blad co w pages/reservation.js. */
  min-width: 0;

  /* react-datepicker opakowuje pole w dwa DIV-y inline-block dopasowane do TRESCI, przez co
     width: 100% na kontenerze zakresu dat nie mial sie do czego odniesc i pole trzymalo
     stale 360px takze na ekranie 390px. */
  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    display: block;
    width: 100%;
    min-width: 0;
  }
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${slideIn} 0.6s ease-out;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px var(--shadow-color);
    border-color: var(--input-border);
  }
`;

export const ClientPanel = styled(Panel)`
  position: relative;
  z-index: 300;
`;

export const TermPanel = styled(Panel)`
  position: relative;
  z-index: 200;
`;

export const PaymentPanel = styled(Panel)`
  position: relative;
  z-index: 150;
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--input-border);
`;

export const PanelTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text);
`;

export const Grid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));

  @media(max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media(max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const RangeLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const ClearRangeButton = styled.button`
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--text-muted);
  font-size: 0.65rem;
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 600;
  letter-spacing: .5px;
  cursor: pointer;
  transition: all .2s;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover:not(:disabled) {
    color: var(--danger);
    border-color: var(--danger);
    background: rgba(239,68,68,0.08);
  }
  &:disabled {
    opacity: .4;
    cursor: not-allowed;
  }
`;

export const InputWrapper = styled.div`
  flex: 1;
  position: relative;
`;

export const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.error ? 'var(--danger)' : 'var(--input-border)'};
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: var(--input-bg);
  transition: all 0.2s ease;
  box-sizing: border-box;
  color: var(--text);

  &:focus {
    outline: none;
    border-color: ${props => props.error ? 'var(--danger)' : 'var(--primary)'};
    background: var(--card-bg);
    box-shadow: ${props => props.error
      ? '0 0 0 4px rgba(239, 68, 68, 0.15)'
      : '0 0 0 4px rgba(59, 130, 246, 0.15)'};
  }

  &::placeholder {
    color: rgba(100,116,139,0.38);
    font-weight: 400;
  }

  &:not(:placeholder-shown) {
    color: var(--text);
    font-weight: 500;
  }
`;

export const SuggestionsList = styled.div`
  position: absolute;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 8px;
  box-shadow: 0 8px 32px var(--shadow-color);
  max-height: 220px;
  overflow-y: auto;
`;

export const SuggestionItem = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  transition: background .12s;
  font-size: 0.95rem;
  color: var(--text);
  display: flex;
  flex-direction: column;
  gap: 2px;

  &:hover { background: var(--table-row-hover); }
`;

export const RangeInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--input-bg);
  border: 2px solid var(--input-border);
  border-radius: 14px;
  padding: 8px;
  min-height: 56px;
  cursor: pointer;
  transition: border-color .2s, box-shadow .2s, background .2s;
  font-size: 0.95rem;
  max-width: 360px;
  width: 100%;

  &:focus-within {
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }

  input {
    flex: 1;
    /* Bez tego pole nie schodzi ponizej swojej naturalnej szerokosci i dwa pola wymuszaja 360px. */
    min-width: 0;
    background: transparent;
    border: 0;
    outline: none;
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--text);
    line-height: 1.2;
    padding: 0;
    cursor: pointer;
  }

  input::placeholder {
    color: rgba(100,116,139,0.38);
    font-weight: 400;
  }

  .range-sep {
    color: var(--text-muted);
  }
`;

export const PaymentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 12px;

  @media(max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const CompactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: stretch;

  & > div {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  @media(max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

export const FormPanel = styled.div`
  background: var(--panel-bg);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid var(--input-border);
`;

export const FormPanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--input-border);
`;

export const FormPanelIcon = IconBadge;

export const FormPanelTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text);
`;

export const FormValue = styled.div`
  padding: 12px 16px;
  background: var(--input-bg);
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 1rem;
  color: var(--text);
  min-height: 48px;
  display: flex;
  align-items: center;
  font-weight: 500;
`;

export const FormLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 8px;
`;

export const FormInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const DepositInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
    background: var(--card-bg);
  }

  &:disabled {
    background: var(--table-row-hover);
    color: var(--text-muted);
    cursor: not-allowed;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
    display: none;
  }
  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;

export const EquipmentButton = styled.button`
  background: ${props => props.enabled ?
    'var(--success)' :
    'var(--text-muted)'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.enabled ? 'pointer' : 'not-allowed'};
  transition: all 0.2s ease;
  box-shadow: var(--shadow-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
`;

export const EquipmentList = styled.div`
  background: var(--panel-bg);
  border-radius: 12px;
  padding: 16px;
  margin-top: 8px;
  border: 1px solid var(--input-border);
`;

export const EquipmentItem = styled.div`
  padding: 8px 12px;
  margin-bottom: 6px;
  background: var(--card-bg);
  border-radius: 8px;
  border: 1px solid var(--input-border);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;

  &:last-child { margin-bottom: 0; }
`;

export const EquipmentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

export const EquipmentPrice = styled.span`
  font-weight: 600;
  color: var(--primary);
  margin-right: 12px;
`;

export const RemoveButton = styled.button`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: var(--danger);
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.4);
    transform: scale(1.1);
  }
`;

export const SummaryTable = styled.div`
  background: var(--card-bg);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--input-border);
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid var(--input-border);
  font-size: 0.95rem;

  &:last-child {
    border-bottom: none;
    background: rgba(59,130,246,0.08);
    font-weight: 700;
    font-size: 1.05rem;
  }
`;

export const SummaryLabel = styled.span`
  font-weight: 600;
  color: var(--text);
`;

export const SummaryValue = styled.span`
  font-weight: 600;
  color: var(--text);
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 24px;

  @media(max-width: 768px) {
    justify-content: stretch;
  }
`;

export const PrimaryButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-primary);
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-primary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media(max-width: 768px) {
    flex: 1;
  }

  /* Na telefonie przyciski akcji stoja w jednym wierszu i oddaja sobie szerokosc, przez co
     etykiety byly przycinane ("Wyslij oferte emailem" miescilo sie w 73px). Wlasny wiersz na
     pelna szerokosc, tak samo jak w pages/reservation.js.
     SuccessButton rozszerza PrimaryButton, wiec dziedziczy te regule. */
  @media (max-width: 560px) {
    flex: 1 1 100%;
    justify-content: center;
  }
`;

export const SuccessButton = styled(PrimaryButton)`
  background: var(--success);
  box-shadow: var(--shadow-sm);

  &:hover:not(:disabled) {
    box-shadow: var(--shadow-md);
  }
`;

export const EditHeaderButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: var(--shadow-primary);

  svg { width: 16px; height: 16px; }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
`;

export const SecondaryButton = styled.button`
  background: var(--input-bg);
  color: var(--text);
  border: 2px solid var(--input-border);
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: var(--table-row-hover);
    border-color: var(--primary);
    color: var(--primary);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    transform: none;
  }

  @media(max-width: 768px) {
    flex: 1;
  }

  /* Na telefonie przyciski akcji stoja w jednym wierszu i oddaja sobie szerokosc, przez co
     etykiety byly przycinane ("Wyslij oferte emailem" miescilo sie w 73px). Wlasny wiersz na
     pelna szerokosc, tak samo jak w pages/reservation.js.
     SuccessButton rozszerza PrimaryButton, wiec dziedziczy te regule. */
  @media (max-width: 560px) {
    flex: 1 1 100%;
    justify-content: center;
  }
`;

export const Message = styled.div`
  margin-bottom: 20px;
  padding: 14px 18px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  background: ${props => props.error ? 'var(--danger-bg)' : props.$warning ? 'var(--warning-bg)' : 'var(--success-bg)'};
  color: ${props => props.error ? 'var(--danger)' : props.$warning ? 'var(--warning)' : 'var(--success)'};
  border: 1px solid ${props => props.error ? 'var(--danger-border)' : props.$warning ? 'var(--warning-border)' : 'var(--success-border)'};
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-overlay);
`;

export const ModalContent = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  padding: 32px;
  max-width: 900px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px var(--shadow-color);
  border: 1px solid var(--input-border);
  z-index: var(--z-modal);
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--input-border);
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: var(--danger);
  }
`;

export const FilterRow = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(6, 1fr);
  margin-bottom: 20px;
`;

export const FilterModalInput = styled.input`
  padding: 12px 16px;
  font-size: 1rem;
  min-height: 48px;
  box-sizing: border-box;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  background: var(--input-bg);
  color: var(--text);
  font-weight: 500;
  transition: all 0.2s ease;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
    background: var(--card-bg);
  }

  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }
`;

export const FilterSearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--input-border);
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
  margin-bottom: 6px;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;

export const RangeInputsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const RangeSeparator = styled.div`
  text-align: center;
  color: var(--text);
  font-size: 1rem;
  padding: 4px 0;
`;

export const RangeInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 2px solid var(--input-border);
  border-radius: 10px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 0.95rem;
  box-sizing: border-box;
  -moz-appearance: textfield;
  appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const EquipmentGrid = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid var(--input-border);
  border-radius: 0 0 12px 12px;
  background: var(--panel-bg);
`;

export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px;
  border-top: 1px solid var(--input-border);
  background: var(--panel-bg);
`;

export const PaginationButton = styled.button`
  padding: 8px 16px;
  border: 2px solid var(--input-border);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const PaginationInfo = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text);
  min-width: 60px;
  text-align: center;
`;

export const EquipmentHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(59,130,246,0.08);
  border: 1px solid var(--input-border);
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: .06em;
  font-weight: 700;
  color: var(--text);
  gap: 8px;

  .col-checkbox { width: 40px; flex-shrink: 0; }
  .col-inventory-number { flex: 1; min-width: 90px; }
  .col-name { flex: 2.5; }
  .col-size { flex: 0.8; min-width: 70px; }
  .col-brand { flex: 1; min-width: 90px; }
  .col-category { flex: 1.3; min-width: 110px; }
  .col-class { flex: 0.8; min-width: 70px; }
`;

export const EquipmentRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--input-border);
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? 'rgba(59, 130, 246, 0.12)' : 'transparent'};
  font-size: 0.85rem;
  gap: 8px;

  &:hover {
    background: ${props => props.selected ? 'rgba(59,130,246,0.18)' : 'var(--table-row-hover)'};
  }
  &:last-child {
    border-bottom: none;
  }

  .col-checkbox { width: 40px; flex-shrink: 0; display: flex; align-items: center; }
  .col-inventory-number { flex: 1; min-width: 90px; font-weight: 600; color: var(--text-muted); }
  .col-name {
    flex: 2.5;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
  }
  .col-size { flex: 0.8; min-width: 70px; }
  .col-brand { flex: 1; min-width: 90px; }
  .col-category { flex: 1.3; min-width: 110px; }
  .col-class { flex: 0.8; min-width: 70px; }
`;

export const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: var(--primary);
  cursor: pointer;
`;

export const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: visible;
  z-index: 0;
`;

export const FilterDropdownButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  min-height: 44px;
  border: 2px solid var(--input-border);
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 1.2;
  transition: border-color .2s, box-shadow .2s, background .2s;
  gap: 8px;
  box-sizing: border-box;

  & > span:first-child {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 28px);
  }

  &:hover {
    border-color: var(--primary);
  }
  &:focus-visible {
    outline: none;
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
  &[data-open="true"] {
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);

    svg {
      color: var(--primary);
    }
  }
  &[data-placeholder="true"] {
    span:first-child {
      color: var(--text-muted);
      font-weight: 400;
    }
  }

  svg {
    flex-shrink: 0;
    transition: color .2s;
    color: var(--text-muted);
  }
`;

export const FilterOptionsList = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  max-height: 200px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: 0 8px 32px var(--shadow-color);
  overflow-y: auto;
  z-index: var(--z-dropdown);
`;

export const FilterOptionItem = styled.li`
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text);
  cursor: pointer;
  transition: background .15s, color .15s;

  .option-content {
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      color: var(--primary);
      flex-shrink: 0;
    }

    .option-label {
      flex: 1;
    }
  }

  &:hover {
    background: var(--table-row-hover);
  }

  &[data-selected="true"] {
    background: rgba(59,130,246,0.15);
    color: var(--primary);
    font-weight: 600;
  }
`;

export const EmptyState = styled.div`
  border: 1px solid var(--input-border);
  background: var(--panel-bg);
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  color: var(--text-muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
  min-height: 120px;
  justify-content: center;

  .icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: rgba(59,130,246,0.12);
    color: var(--primary);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--shadow-primary);
  }

  .text {
    font-weight: 600;
    color: var(--text-muted);
    line-height: 1.3;
  }
`;

export const TabNavigation = styled.div`
  display: flex;
  gap: 2px;
  max-width: 1200px;
  margin: 0 auto 24px auto;
  padding: 0 24px;
  border-bottom: 2px solid var(--input-border);
`;

/* Wzorzec przejęty 1:1 z kalendarza (calendar.styles.js#TabButton) — przezroczyste tło,
   podkreślenie ::after zamiast wypełnionej "pigułki". Ujednolica wygląd zakładek w całej
   aplikacji zamiast osobnego, kontrastowego stylu tylko na stronie oferty. */
export const TabButton = styled.button`
  position: relative;
  padding: 10px 16px 12px;
  border: none;
  background: transparent;
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-muted)'};
  font-weight: 600;
  font-size: 0.92rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  transition: color 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: -2px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${props => props.active ? 'var(--primary)' : 'transparent'};
    transition: background 0.15s ease;
  }

  &:hover {
    color: ${props => props.active ? 'var(--primary)' : 'var(--text)'};
  }

  @media (max-width: 540px) {
    flex: 1;
    justify-content: center;
    padding: 10px 8px 12px;
    font-size: 0.8rem;
    gap: 5px;
    svg { width: 15px; height: 15px; }
  }

  @media (max-width: 380px) {
    font-size: 0.75rem;
    svg { display: none; }
  }
`;

export const HistorySection = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 20px;
`;

export const HistoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const HistoryTitle = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const HistoryList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
`;

export const OfferCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px var(--shadow-color);
    border-color: var(--info);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--primary);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

export const OfferCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const OfferAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.125rem;
  flex-shrink: 0;
`;

export const OfferInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

export const OfferStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid var(--input-border);
`;

export const OfferActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const OfferDeleteButton = styled.button`
  background: none;
  border: 1px solid var(--input-border);
  color: var(--text);
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: var(--danger);
    color: var(--danger);
  }
`;

export const OfferActionButton = styled.button`
  background: none;
  border: 1px solid var(--input-border);
  color: var(--text);
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;

  &:hover {
    background: var(--hover-bg);
    border-color: var(--info);
    color: var(--info);
  }
`;

export const OfferRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

export const OfferName = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text);
  line-height: 1.3;
`;

export const OfferMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--text-muted);
`;

export const OfferPrice = styled.div`
  font-weight: 700;
  font-size: 1.15rem;
  color: var(--primary);
`;

export const OfferBadge = styled.div`
  background: var(--success);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const OfferDate = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const HistoryEmpty = styled.div`
  border: 1px solid var(--input-border);
  background: var(--panel-bg);
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  color: var(--text-muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  font-size: 0.95rem;
`;

export const FiltersPanel = styled.div`
  background: var(--panel-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  /* Warstwa listy rozwijanej, NIE popovera: pasek to zwykly element strony. Wlasny
     z-index jest potrzebny, bo bez niego rozwiniety filtr Status chowalby sie pod
     kartami historii (karty sa pozycjonowane i stoja dalej w drzewie). Na --z-popover
     (1200) pasek przebijal nakladke okna szczegolow oferty (--z-overlay, 1000). */
  z-index: var(--z-dropdown);
`;

export const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 150px;
`;

export const FilterLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const FilterInput = styled.input`
  padding: 10px 12px;
  min-height: 44px;
  box-sizing: border-box;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  background: var(--card-bg);
  color: var(--text);
  font-size: 0.95rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  &[type="number"]::-webkit-outer-spin-button,
  &[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

export const FilterSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid var(--input-border);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--text);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;

export const ClearFiltersButton = styled.button`
  padding: 10px 16px;
  background: transparent;
  border: 1px solid var(--input-border);
  border-radius: 8px;
  color: var(--text);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: var(--danger);
    color: var(--danger);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const OfferFormModal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-overlay);
  backdrop-filter: blur(8px);
  overflow-y: auto;
  padding: 20px;
`;

export const OfferFormContainer = styled.div`
  background: var(--card-bg);
  border-radius: 20px;
  padding: 28px;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 75px var(--shadow-color);
  border: 1px solid var(--input-border);
  position: relative;
  z-index: var(--z-modal);

  ${FilterOptionsList} {
    z-index: var(--z-popover);
  }

  @media(max-width: 768px) {
    padding: 20px;
  }
`;

export const OfferFormHeader = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--input-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const OfferFormTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const DatepickerTheme = createGlobalStyle`
  .react-datepicker {
    width: 252px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    background: var(--card-bg);
    border: 1px solid var(--input-border);
    border-radius: 14px;
    box-shadow: 0 10px 34px var(--shadow-color);
    overflow: hidden;
    padding: 0 0 6px 0;
  }
  .react-datepicker__month { margin: 4px 6px 0 6px; }

  .react-datepicker__day.past-day:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range) {
    color: var(--text-muted);
    opacity: 0.45;
    cursor: not-allowed;
    text-decoration: line-through;
  }

  .react-datepicker__triangle { display: none; }

  .react-datepicker-popper[data-placement^="bottom"],
  .react-datepicker-popper[data-placement^="top"] {
    margin-top: 6px;
    z-index: var(--z-popover) !important;
  }

  .react-datepicker__header {
    background: var(--primary);
    border-bottom: 1px solid rgba(255,255,255,0.15);
    padding-top: 6px;
    padding-bottom: 4px;
  }

  .react-datepicker__current-month {
    color: var(--primary-contrast);
    font-weight: 600;
    font-size: 0.90rem;
    letter-spacing: .3px;
  }

  .react-datepicker__day-names {
    margin-top: 2px;
  }

  .react-datepicker__day-name {
    color: rgba(255,255,255,0.85);
    font-weight: 500;
    width: 32px;
    line-height: 32px;
    margin: 1px;
    font-size: 0.65rem;
  }

  .react-datepicker__week {
    display: flex;
    justify-content: space-between;
  }

  .react-datepicker__day {
    width: 32px;
    height: 32px;
    line-height: 32px;
    margin: 1px;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.78rem;
    color: var(--text);
    transition: all .15s ease;
    outline: none;
  }

  .react-datepicker__day:hover {
    background: var(--table-row-hover);
    cursor: pointer;
  }

  .react-datepicker__day--outside-month {
    opacity: 0.30;
  }

  .react-datepicker__day--today:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range) {
    font-weight: 600;
    box-shadow: inset 0 0 0 2px rgba(59,130,246,0.45);
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background: var(--primary);
    color: var(--primary-contrast) !important;
    box-shadow: var(--shadow-primary);
  }

  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range {
    background: rgba(59,130,246,0.18);
    color: var(--text);
    border-radius: 8px;
  }

  .react-datepicker__day.weekend:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range):not(.react-datepicker__day--keyboard-selected) {
    color: var(--danger);
  }
  .react-datepicker__day.weekend:hover:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range) {
    background: rgba(239,68,68,0.12);
  }

  .react-datepicker__navigation {
    top: 8px;
  }
  .react-datepicker__navigation-icon::before {
    border-color: var(--primary-contrast);
  }

  [data-theme='dark'] .react-datepicker {
    background: var(--panel-bg);
  }
`;
